import { CheckmarkIcon, CloseIcon, CopyIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { DNSRecord } from '@va/shared/api/custom-domains';
import { CommonProps } from '@va/types/component';
import { CopyToClipboardArea } from '@va/ui/components/copy-to-clipboard-area';
import { Button, fontWeights, Paragraph, paragraphSizes, ParagraphWithTooltip } from '@va/ui/design-system';
import classNames from 'classnames';
import { PropsWithChildren, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';

export const DNSTableRow = ({
  record,
  isLoading,
  verified,
}: {
  record?: DNSRecord;
  isLoading: boolean;
  verified?: boolean;
}) => {
  const translate = useTranslate();

  const recordType = useMemo(() => {
    if (!record) return '-';
    if (record.type === 'TXTContains') return 'TXT';
    return record.type;
  }, [record]);

  return (
    <div className='contents'>
      <DNSRecordBadge isLoading={isLoading} className='min-w-120px mb-3 mr-1 px-3 rounded-l-12'>
        <div>
          <Paragraph size={paragraphSizes.tiny} colorClassName='text-gray-devil'>
            {translate('customDomain.dnsRecordsTable.headings.type')}
          </Paragraph>
          <Paragraph size={paragraphSizes.medium} weight={fontWeights.semibold}>
            {recordType}
          </Paragraph>
        </div>
      </DNSRecordBadge>
      <DNSRecordBadge isLoading={isLoading} className='min-w-120px px-3 mb-3 mr-1'>
        <div>
          <Paragraph size={paragraphSizes.tiny} colorClassName='text-gray-devil'>
            {translate('customDomain.dnsRecordsTable.headings.hostname')}
          </Paragraph>
          <Paragraph size={paragraphSizes.medium} weight={fontWeights.semibold}>
            {record?.name ?? '-'}
          </Paragraph>
        </div>
      </DNSRecordBadge>
      <DNSRecordBadge
        isLoading={isLoading}
        className='min-w-150px gap-5 px-3 justify-between rounded-r-12 w-full overflow-hidden'
      >
        <div className='flex  w-full items-center justify-between overflow-hidden'>
          <div className='w-2/3'>
            <Paragraph size={paragraphSizes.tiny} colorClassName='text-gray-devil'>
              {translate('customDomain.dnsRecordsTable.headings.value')}
            </Paragraph>

            <ParagraphWithTooltip
              tooltipProps={{ interactive: true }}
              size={paragraphSizes.medium}
              weight={fontWeights.semibold}
            >
              {`${record?.value ?? '-'}`}
            </ParagraphWithTooltip>
          </div>

          {verified === true && (
            <Button className={'!bg-green-400'} icon={() => <CheckmarkIcon color='white' />} color='quaternary' />
          )}
          {verified === false && (
            <Button
              className={'!bg-red-600'}
              icon={() => <CloseIcon className='w-5 h-5' color='white' />}
              color='quaternary'
            />
          )}
          {verified === undefined && (
            <CopyToClipboardArea
              content={record?.value ?? ''}
              anchor={(success) => {
                return (
                  <Button
                    className={classNames({ '!bg-green-400': success })}
                    icon={(_, color) => {
                      if (success) return <CheckmarkIcon color='white' />;
                      return <CopyIcon color={color} />;
                    }}
                    color='quaternary'
                  />
                );
              }}
            />
          )}
        </div>
      </DNSRecordBadge>
    </div>
  );
};

export const DNSRecordBadge = ({
  children,
  className,
  isLoading,
}: PropsWithChildren<CommonProps & { isLoading?: boolean }>) => {
  return (
    <div className={classNames('relative h-60px flex items-center bg-gray-concrete text-gray-devil', className)}>
      {isLoading ? (
        <div className='rounded-15 absolute overflow-hidden top-0 left-0 h-full w-full p-1 '>
          <Skeleton className='rounded-15 h-[2.8125rem]' />
        </div>
      ) : (
        children
      )}
    </div>
  );
};
